import { formatDistance } from 'date-fns'
import { observer } from 'mobx-react'
import React, { ReactElement, ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Copy from '../icons/Copy'
import OpenBankingPaymentController from '../state/OpenBankingPaymentController'
import Radom, { ICheckoutSession, OpenBankingPaymentSummary, OpenBankingPaymentView } from '../state/Radom'
import {
  formatFiatAmount,
  isOpenBankingPaymentFinal,
  isOpenBankingPaymentInFlight,
  isOpenBankingPaymentView,
  openBankingBankLabel,
  openBankingHistoryPresentation,
  openBankingStatusPresentation,
  openBankingViewFromSession
} from '../util/OpenBankingPayment'
import { usePermissions } from '../util/permissions'
import { errorToast, infoToast } from '../util/Util'
import { PrimaryButton, SecondaryButton } from './Button'
import { InlineNotice, InlinePill, PillTone } from './DashboardPrimitives'
import DetailsDrawer, { DrawerCard, DrawerDetailLabel, DrawerDetailRow, DrawerDetailValue } from './DetailsDrawer'
import Modal from './Modal'
import RefundForm from './RefundForm'
import Spinner from './Spinner'

const StatusCard = styled(DrawerCard)`
  gap: 10px;
`

const StatusTop = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
`

const StatusPill = styled(InlinePill)`
  font-size: 12px;
  padding: 6px 10px;
`

const StatusUpdated = styled.span`
  color: var(--text-muted, #768092);
  font-size: 12px;
`

const StatusDescription = styled.p`
  color: var(--ink, #20242d);
  font-size: 13.5px;
  line-height: 1.5;
  margin: 0;
`

const CardTitle = styled.span`
  color: var(--text-faint, #7b8494);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  margin-bottom: 10px;
  text-transform: uppercase;
`

const Note = styled(InlineNotice)`
  display: block;
  font-size: 12.5px;
  line-height: 1.45;
`

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
`

const CopyButton = styled.button`
  align-items: center;
  background: none;
  border: 0;
  color: var(--text-faint, #7b8494);
  cursor: pointer;
  display: inline-flex;
  margin-left: 6px;
  padding: 0;

  &:hover {
    color: var(--ink, #20242d);
  }
`

const ValueLine = styled.span`
  align-items: center;
  display: inline-flex;
  justify-content: flex-end;
  max-width: 100%;
  min-width: 0;
`

const Timeline = styled.ol`
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: 0;
`

const dotColors: Record<PillTone, string> = {
  green: 'var(--pill-green-ink, #16724b)',
  amber: 'var(--pill-amber-ink, #a85014)',
  red: 'var(--pill-red-ink, #c51735)',
  blue: 'var(--pill-blue-ink, #3157c6)',
  neutral: 'var(--text-faint, #9299a8)'
}

const TimelineItem = styled.li<{ $tone: PillTone, $last: boolean }>`
  display: grid;
  gap: 0 12px;
  grid-template-columns: 12px minmax(0, 1fr);
  padding-bottom: ${({ $last }) => $last ? 0 : 14}px;
  position: relative;

  &::before {
    background: ${({ $tone }) => dotColors[$tone]};
    border-radius: 50%;
    content: '';
    height: 9px;
    margin-top: 5px;
    width: 9px;
  }

  &::after {
    background: var(--border, #e5ebf3);
    bottom: 0;
    content: '';
    display: ${({ $last }) => $last ? 'none' : 'block'};
    left: 4px;
    position: absolute;
    top: 18px;
    width: 1px;
  }
`

const TimelineTitle = styled.span`
  color: var(--ink, #20242d);
  display: block;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
`

const TimelineDetail = styled.span`
  color: var(--text-muted, #768092);
  display: block;
  font-size: 12.5px;
  line-height: 1.4;
`

const TimelineTime = styled.span`
  color: var(--text-faint, #9299a8);
  display: block;
  font-size: 11.5px;
  margin-top: 2px;
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Centered = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 28px 0;
`

const copy = (value: string, what: string): void => {
  navigator.clipboard.writeText(value)
    .then(() => infoToast(`Copied ${what}`))
    .catch(() => errorToast('Could not copy to the clipboard'))
}

const Row = (props: { label: string, children: ReactNode }): ReactElement => <DrawerDetailRow>
  <DrawerDetailLabel>{props.label}</DrawerDetailLabel>
  <DrawerDetailValue>{props.children}</DrawerDetailValue>
</DrawerDetailRow>

const CopyRow = (props: { label: string, value?: string, what: string, mono?: boolean }): ReactElement | null => {
  if (!props.value) return null
  const value = props.value
  return <Row label={props.label}>
    <ValueLine>
      {props.mono ? <Mono>{value}</Mono> : <span>{value}</span>}
      <CopyButton type='button' aria-label={`Copy ${props.what}`} onClick={() => copy(value, props.what)}>
        <Copy style={{ width: 14, height: 14 }} />
      </CopyButton>
    </ValueLine>
  </Row>
}

const TextRow = (props: { label: string, value?: string }): ReactElement | null =>
  props.value ? <Row label={props.label}>{props.value}</Row> : null

const formatDateTime = (value?: string): string => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

const relativeTime = (value?: string): string => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : formatDistance(date, new Date(), { addSuffix: true })
}

/**
 * A credited amount to the precision it was booked at (up to six decimals),
 * so the figures add up exactly against the balance and the exports.
 */
const formatCredited = (amount: number | string, ticker?: string): string => {
  const value = Number(amount)
  const formatted = Number.isFinite(value)
    ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })
    : String(amount)
  return ticker ? `${formatted} ${ticker}` : formatted
}

/** The body of the drawer: everything a merchant needs about one bank payment. */
export const OpenBankingPaymentContent = (props: {
  payment: OpenBankingPaymentView | OpenBankingPaymentSummary
  /** Leave the refunds out where the page already lists them. */
  hideRefunds?: boolean
  /** Leave the session metadata out where the page already lists it. */
  hideMetadata?: boolean
}): ReactElement => {
  const { payment } = props
  const view = isOpenBankingPaymentView(payment) ? payment : undefined
  const presentation = openBankingStatusPresentation(payment.status)
  const inFlight = isOpenBankingPaymentInFlight(payment.status)
  const sessionExpired = !!payment.expiresAt && new Date(payment.expiresAt).getTime() < Date.now()
  const credit = view?.credit
  const creditedTicker = credit?.ticker ?? 'USDC'
  const payer = view?.payer
  const bankLabel = openBankingBankLabel(view?.bankName, view?.bankId)
  // The provider's history ends where the customer stopped. When the session
  // then ran out, that is the last thing that happened to the payment.
  const history = view
    ? [
      ...view.history.map(entry => ({ ...openBankingHistoryPresentation(entry, !!credit), at: entry.at })),
      ...(payment.status === 'notCompleted' && sessionExpired && payment.expiresAt
        ? [{
          title: 'Checkout session expired',
          detail: 'The customer had not approved the payment. Nothing was paid.',
          tone: 'neutral' as PillTone,
          at: payment.expiresAt
        }]
        : [])
    ]
    : []

  return <>
    <StatusCard>
      <StatusTop>
        <StatusPill $tone={presentation.tone}>{presentation.label}</StatusPill>
        <StatusUpdated>Updated {relativeTime(payment.updatedAt)}</StatusUpdated>
      </StatusTop>
      <StatusDescription>{presentation.description}</StatusDescription>
    </StatusCard>

    {
      inFlight && sessionExpired &&
      <Note>
        The checkout session has passed its expiry time, but the customer approved this payment before it did.
        The payment is still open and is credited if it completes.
      </Note>
    }

    <DrawerCard>
      <CardTitle>Amounts</CardTitle>
      <Row label='Payment amount'>{formatFiatAmount(payment.amount, payment.currency)}</Row>
      {
        credit
          ? <>
            <Row label={`Converted to ${creditedTicker}`}>{formatCredited(credit.grossAmount, creditedTicker)}</Row>
            <Row label='Fee'>− {formatCredited(credit.feeAmount, creditedTicker)}</Row>
            <Row label='Added to your balance'>
              <strong>{formatCredited(credit.netAmount, creditedTicker)}</strong>
            </Row>
            <TextRow label='Credited on' value={formatDateTime(credit.creditedAt)} />
          </>
          : <Row label='Added to your balance'>
            {isOpenBankingPaymentFinal(payment.status) ? 'Nothing' : 'Not yet'}
          </Row>
      }
    </DrawerCard>

    {
      history.length > 0 &&
      <DrawerCard>
        <CardTitle>What happened</CardTitle>
        <Timeline>
          {history.map((step, i) =>
            <TimelineItem key={`${step.title}-${step.at}-${i}`} $tone={step.tone} $last={i === history.length - 1}>
              <div>
                <TimelineTitle>{step.title}</TimelineTitle>
                {step.detail && <TimelineDetail>{step.detail}</TimelineDetail>}
                <TimelineTime>{formatDateTime(step.at)}</TimelineTime>
              </div>
            </TimelineItem>
          )}
        </Timeline>
      </DrawerCard>
    }

    {
      view && (payer || bankLabel || view.paymentScheme) &&
      <DrawerCard>
        <CardTitle>Paid from</CardTitle>
        <TextRow label='Account holder' value={payer?.name} />
        <CopyRow label='IBAN' value={payer?.iban} what='IBAN' mono />
        <TextRow label='BIC' value={payer?.bic} />
        <TextRow label='Sort code' value={payer?.sortCode} />
        <TextRow label='Account number' value={payer?.accountNumber} />
        <TextRow label='Bank' value={bankLabel} />
        <TextRow label='Bank country' value={view.bankMarket} />
        <TextRow label='Payment rail' value={view.paymentScheme} />
      </DrawerCard>
    }

    {
      view && (view.bankStatementReference || view.paymentReference || view.endToEndId || view.remittanceInformation) &&
      <DrawerCard>
        <CardTitle>References</CardTitle>
        <CopyRow label='Bank statement reference' value={view.bankStatementReference} what='reference' mono />
        <CopyRow label='Payment reference' value={view.paymentReference} what='reference' mono />
        <CopyRow label='End-to-end ID' value={view.endToEndId} what='end-to-end ID' mono />
        <TextRow label='Remittance information' value={view.remittanceInformation} />
      </DrawerCard>
    }

    <DrawerCard>
      <CardTitle>Customer and session</CardTitle>
      <CopyRow label='Customer email' value={view?.customerEmail ?? payment.customerEmail} what='email' />
      <TextRow label='Customer phone' value={view?.customerPhone} />
      <CopyRow label='Checkout session' value={payment.checkoutSessionId} what='checkout session ID' mono />
      <TextRow label='Started' value={formatDateTime(payment.createdAt)} />
      {
        payment.expiresAt && !credit &&
        <Row label={sessionExpired ? (inFlight ? 'Session expiry time' : 'Session expired') : 'Session expires'}>
          {formatDateTime(payment.expiresAt)}
        </Row>
      }
      {
        !props.hideMetadata &&
        (view?.metadata ?? []).map(item => <TextRow key={item.key} label={item.key} value={item.value} />)
      }
    </DrawerCard>

    {
      view && !props.hideRefunds && view.refunds.length > 0 &&
      <DrawerCard>
        <CardTitle>Refunds</CardTitle>
        {view.refunds.map((refund, i) => <Row key={`${refund.createdAt}-${i}`} label={formatDateTime(refund.createdAt)}>
          <ValueLine style={{ gap: 8 }}>
            <span>{Number(refund.amount)}</span>
            <InlinePill $tone={refund.status === 'success' ? 'green' : refund.status === 'failed' ? 'red' : 'amber'}>
              {refund.status}
            </InlinePill>
          </ValueLine>
        </Row>)}
      </DrawerCard>
    }
  </>
}

/**
 * The single place an open-banking payment is shown. Mounted once at the App
 * root and opened through `OpenBankingPaymentController`, so search, lists and
 * detail pages all lead to the same view of the same payment.
 */
const OpenBankingPaymentDrawer = observer((): ReactElement | null => {
  const checkoutSessionId = OpenBankingPaymentController.checkoutSessionId
  const seed = OpenBankingPaymentController.seed
  const navigate = useNavigate()
  const { has } = usePermissions()

  const [view, setView] = useState<OpenBankingPaymentView>()
  const [isLoading, setIsLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [refundSession, setRefundSession] = useState<ICheckoutSession>()
  const [isLoadingRefund, setIsLoadingRefund] = useState(false)

  const load = (id: string, isCancelled: () => boolean): void => {
    setIsLoading(true)
    setLoadFailed(false)
    Radom.getOpenBankingPayment(id)
      .catch(async () => {
        // An API build without the payment view: assemble it from the session.
        const session = await Radom.getCheckoutSession(id, true)
        const fallback = openBankingViewFromSession(session)
        if (!fallback) throw new Error('Not an open banking session')
        return fallback
      })
      .then(payment => { if (!isCancelled()) setView(payment) })
      .catch(err => {
        console.error('Failed to load the open banking payment', err)
        if (!isCancelled()) setLoadFailed(true)
      })
      .finally(() => { if (!isCancelled()) setIsLoading(false) })
  }

  useEffect(() => {
    setView(undefined)
    setRefundSession(undefined)
    if (!checkoutSessionId) return

    let cancelled = false
    load(checkoutSessionId, () => cancelled)
    return () => { cancelled = true }
  }, [checkoutSessionId])

  if (!checkoutSessionId) return null

  const payment = view ?? seed
  const close = OpenBankingPaymentController.close
  const goTo = (path: string): void => {
    close()
    navigate(path)
  }

  const startRefund = (): void => {
    setIsLoadingRefund(true)
    Radom.getCheckoutSession(checkoutSessionId)
      .then(setRefundSession)
      .catch(err => errorToast(err.response?.data?.error || err.message))
      .finally(() => setIsLoadingRefund(false))
  }

  return <>
    <DetailsDrawer
      visible
      onClose={close}
      eyebrow='Open banking payment'
      title={payment ? formatFiatAmount(payment.amount, payment.currency) : 'Bank payment'}
      subtitle={payment?.customerEmail}
      width={520}
      ariaLabel='Open banking payment details'
    >
      {payment && <OpenBankingPaymentContent payment={payment} />}
      {isLoading && <Centered><Spinner /></Centered>}
      {
        loadFailed && !isLoading &&
        <Note>
          The details of this payment could not be loaded. {payment ? 'The status above is the last one we know of.' : ''}
        </Note>
      }
      <Actions>
        {
          view?.paymentEventId &&
          <SecondaryButton
            type='button'
            onClick={() => goTo(`/transactions?selected=${view.paymentEventId}&q=${view.paymentEventId}`)}>
            View in transactions
          </SecondaryButton>
        }
        <SecondaryButton type='button' onClick={() => goTo(`/checkout_session/${checkoutSessionId}`)}>
          Open checkout session
        </SecondaryButton>
        {
          view?.refundable && has('WriteRefunds') &&
          <PrimaryButton type='button' isLoading={isLoadingRefund} onClick={startRefund}>
            Refund
          </PrimaryButton>
        }
      </Actions>
    </DetailsDrawer>

    {
      refundSession &&
      <Modal visible onClose={() => setRefundSession(undefined)} title='Refund payment'>
        <div style={{ width: 500, maxWidth: '100%', padding: '0 30px 30px 30px' }}>
          <RefundForm
            checkoutSession={refundSession}
            onRefund={() => {
              setRefundSession(undefined)
              load(checkoutSessionId, () => false)
            }} />
        </div>
      </Modal>
    }
  </>
})

export default OpenBankingPaymentDrawer
